// frontend/src/components/TransacaoModal/TransacaoModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, DatePicker, Radio, Button, Alert, Row, Col, Spin, Checkbox, AutoComplete, Input, InputNumber, Switch, App } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { createTransacao, updateTransacao, getTransacaoById, createLancamentoParceladoConta } from '../../services/transactionService';
import { getDescricaoSuggestions, deleteDescricaoSuggestion } from '../../services/suggestionService';
import { mapDescricaoSuggestions } from '../../utils/descricaoSuggestions';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import { modalPopupContainer, scrollFieldIntoView } from '../../utils/mobileModal';
import { currencyInputProps } from '../../utils/numberInputMask';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import CobrancaModal from '../CobrancaModal/CobrancaModal';
import PixCobrancaSetupModal, { needsPixCobrancaSetup } from '../PixCobrancaSetupModal/PixCobrancaSetupModal';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';
import { getClientes } from '../../services/clienteService';
import { temModoPrestador } from '../../utils/moduloAccess';

const { Option } = Select;

const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const moneyInputProps = currencyInputProps({ inputMode: 'decimal' });

/** Resumo ao vivo do parcelado (total ↔ parcela). */
function ParceladoResumo({ form, modoParcelado, formatCurrency }) {
    const valorTotal = Form.useWatch('valor_total_parcelado', form);
    const valorParcela = Form.useWatch('valor_parcela', form);
    const totalParcelas = Form.useWatch('total_parcelas', form);
    const n = parseInt(totalParcelas, 10);
    if (!n || n < 2) return null;
    let total = 0;
    let parcela = 0;
    if (modoParcelado === 'valor_total') {
        total = parseFloat(valorTotal);
        parcela = total && n ? (total / n) : 0;
    } else {
        parcela = parseFloat(valorParcela);
        total = parcela && n ? parcela * n : 0;
    }
    if (!total && !parcela) return null;
    return (
        <div style={{ fontSize: 12, color: '#595959', marginTop: 4, padding: '6px 0' }}>
            {modoParcelado === 'valor_total'
                ? `${n} parcelas de ${formatCurrency(parcela)} = Total ${formatCurrency(total)}`
                : `${formatCurrency(parcela)} × ${n} = Total ${formatCurrency(total)}`}
        </div>
    );
}

// 1. ADICIONADA A PROP isMobile
const TransacaoModal = ({ isOpen, onClose, onSave, transacaoToEdit, contas = [], allCategorias = [], isLoadingData = false, isMobile = false }) => {
    const { modal, message } = App.useApp();
    const { user, temModoPrestador: modoPrestadorCtx } = useAuth();
    const modoPrestador = modoPrestadorCtx ?? temModoPrestador(user);
    const workspace = useWorkspace();
    const compartilhamentoAtivo = !!(
        workspace?.emWorkspaceCompartilhado
        || workspace?.concedido
        || (workspace?.recebidos && workspace.recebidos.length > 0)
    );
    const criadoPorNome = transacaoToEdit?.criado_por_nome;
    const lancadoPor = compartilhamentoAtivo && criadoPorNome
        ? (transacaoToEdit?.criado_por_id === user?.id ? 'você' : criadoPorNome)
        : null;
    const [form] = Form.useForm();
    const [error, setError] = useState('');
    const [formType, setFormType] = useState('despesa');
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingDetails, setIsFetchingDetails] = useState(false);
    const [isEditingTransfer, setIsEditingTransfer] = useState(false);
    const [metaVinculada, setMetaVinculada] = useState(null);
    const [isSavingAndNew, setIsSavingAndNew] = useState(false);
    const [marcarComoPago, setMarcarComoPago] = useState(false);
    const [ehCobranca, setEhCobranca] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isParcelado, setIsParcelado] = useState(false);
    const [modoParcelado, setModoParcelado] = useState('valor_total');
    const [isCobrancaModalOpen, setIsCobrancaModalOpen] = useState(false);
    const [transacaoCobranca, setTransacaoCobranca] = useState(null);
    const [isPixSetupOpen, setIsPixSetupOpen] = useState(false);
    const [clientes, setClientes] = useState([]);

    const handleRemoverSugestao = useCallback((sugestao) => {
        modal.confirm({
            title: 'Remover sugestão',
            content: `"${sugestao.descricao}" não vai mais aparecer nas sugestões de descrição. Lançamentos já salvos não são afetados.`,
            okText: 'Remover',
            okButtonProps: { danger: true },
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteDescricaoSuggestion(sugestao.id);
                    setSuggestions((prev) => prev.filter((o) => o.id !== sugestao.id));
                    message.success('Sugestão removida.');
                } catch (err) {
                    message.error(getErrorMessage(err, 'Não foi possível remover a sugestão.'));
                }
            },
        });
    }, [modal, message]);

    useEffect(() => {
        if (searchTerm.length < 2) {
            setSuggestions([]);
            return;
        }
        const fetchSuggestions = async () => {
            const results = await getDescricaoSuggestions(searchTerm);
            setSuggestions(mapDescricaoSuggestions(results, { onDelete: handleRemoverSugestao }));
        };
        const delayDebounceFn = setTimeout(() => {
            fetchSuggestions();
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, handleRemoverSugestao]);

    useEffect(() => {
        if (!isOpen || !modoPrestador) {
            setClientes([]);
            return;
        }
        let cancelled = false;
        getClientes()
            .then((data) => {
                if (!cancelled) setClientes(Array.isArray(data) ? data : data?.results || []);
            })
            .catch(() => {
                if (!cancelled) setClientes([]);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, modoPrestador]);

    const isEditing = !!transacaoToEdit;
    const isTransferencia = formType === 'transferencia';

    useEffect(() => {
        if (!isOpen) {
            setIsEditingTransfer(false);
            setMarcarComoPago(false);
            setEhCobranca(false);
            setSuggestions([]);
            setSearchTerm('');
            setIsParcelado(false);
            setModoParcelado('valor_total');
            form.resetFields(); // Reseta o form ao fechar
            setMetaVinculada(null);
            return;
        }

        const setupModalForEditing = async () => {
            if (isEditing && transacaoToEdit?.id) {
                setIsFetchingDetails(true);
                setError('');
                try {
                    const transacaoData = await getTransacaoById(transacaoToEdit.id);
                    const tipo = transacaoData.tipo || 'despesa';
                    setFormType(tipo);
                    if (tipo === 'transferencia') {
                        setIsEditingTransfer(true);
                    }
                    if (transacaoData.meta_poupanca) {
                        setMetaVinculada({
                            id: transacaoData.meta_poupanca,
                            nome: transacaoData.meta_poupanca_nome,
                            movimento: transacaoData.movimento_meta,
                        });
                    } else {
                        setMetaVinculada(null);
                    }
                    const isPago = transacaoData.status === 'PAGO';
                    setMarcarComoPago(isPago);
                    setEhCobranca(transacaoData.eh_cobranca || false);

                    form.setFieldsValue({
                        ...transacaoData,
                        valor: parseFloat(transacaoData.valor) || null,
                        data_vencimento: transacaoData.data_vencimento ? dayjs(transacaoData.data_vencimento) : null,
                        marcar_como_pago: isPago,
                        data_pagamento: transacaoData.data_pagamento ? dayjs(transacaoData.data_pagamento) : null,
                        eh_cobranca: transacaoData.eh_cobranca || false,
                        nome_devedor: transacaoData.nome_devedor || '',
                        cliente: transacaoData.cliente || undefined,
                    });
                } catch (err) {
                    console.error("Erro ao carregar dados da transação para edição:", err);
                    setError(getErrorMessage(err, "Falha ao carregar os detalhes da transação."));
                } finally {
                    setIsFetchingDetails(false);
                }
            } else {
                form.resetFields();
                setFormType('despesa');
                setMarcarComoPago(false);
                setEhCobranca(false);
                setIsParcelado(false);
                setModoParcelado('valor_total');
                setMetaVinculada(null);
                setIsEditingTransfer(false);
                form.setFieldsValue({
                    tipo: 'despesa',
                    data_vencimento: dayjs(),
                    valor: 0,
                    marcar_como_pago: false,
                    eh_cobranca: false,
                    parcelado: false,
                    modo_parcelado: 'valor_total',
                    total_parcelas: 2,
                    valor_total_parcelado: null,
                    valor_parcela: null,
                });
            }
        };
        setupModalForEditing();
    }, [isOpen, isEditing, transacaoToEdit, form]);

    const handleSearch = (value) => { setSearchTerm(value); };
    const handleSelect = (value, option) => {
        setSearchTerm(value);
        form.setFieldsValue({ descricao: option.value, categoria: option.categoria_id });
    };

    const handleFormValuesChange = (changedValues) => {
        if (changedValues.tipo) {
            setFormType(changedValues.tipo);
            form.setFieldsValue({ categoria: null, conta: null, conta_destino: null });
            if (changedValues.tipo !== 'receita') {
                setEhCobranca(false);
                form.setFieldsValue({ eh_cobranca: false, nome_devedor: '' });
            }
        }
        if ('marcar_como_pago' in changedValues) {
            const isChecked = changedValues.marcar_como_pago;
            setMarcarComoPago(isChecked);
            if (isChecked && !form.getFieldValue('data_pagamento')) {
                form.setFieldsValue({ data_pagamento: dayjs() });
            }
        }
        // descrição → searchTerm só via onSearch do AutoComplete (evita re-render a cada tecla)
        if ('eh_cobranca' in changedValues) {
            setEhCobranca(changedValues.eh_cobranca);
            if (changedValues.eh_cobranca && needsPixCobrancaSetup(user)) {
                setIsPixSetupOpen(true);
            }
            if (!changedValues.eh_cobranca) {
                form.setFieldsValue({ cliente: undefined, nome_devedor: '' });
            }
        }
        if ('cliente' in changedValues) {
            const clienteId = changedValues.cliente;
            const found = clientes.find((c) => c.id === clienteId);
            if (found) {
                form.setFieldsValue({ nome_devedor: found.nome });
            }
        }
        if ('parcelado' in changedValues) {
            setIsParcelado(changedValues.parcelado);
            if (!changedValues.parcelado) {
                form.setFieldsValue({ valor_total_parcelado: null, valor_parcela: null, total_parcelas: 2 });
            }
        }
        if ('modo_parcelado' in changedValues) {
            setModoParcelado(changedValues.modo_parcelado);
            // Limpa só o campo inativo — evita matar o valor enquanto o teclado está no ativo
            if (changedValues.modo_parcelado === 'valor_total') {
                form.setFieldsValue({ valor_parcela: null });
            } else {
                form.setFieldsValue({ valor_total_parcelado: null });
            }
        }
    };
    
    const buildPayload = (values) => {
        const payload = { 
            ...values, 
            valor: parseFloat(values.valor) || 0, 
            data_vencimento: values.data_vencimento ? dayjs(values.data_vencimento).format('YYYY-MM-DD') : null,
            status: values.marcar_como_pago ? 'PAGO' : 'PENDENTE',
            data_pagamento: (values.marcar_como_pago && values.data_pagamento) ? dayjs(values.data_pagamento).format('YYYY-MM-DD') : null,
            eh_cobranca: values.eh_cobranca || false,
            nome_devedor: values.eh_cobranca ? (values.nome_devedor || null) : null,
            cliente: values.eh_cobranca ? (values.cliente || null) : null,
        };
        if (isTransferencia) {
            payload.categoria = null;
            payload.status = 'PAGO';
            payload.data_pagamento = dayjs(values.data_vencimento).format('YYYY-MM-DD');
            payload.eh_cobranca = false;
            payload.nome_devedor = null;
            payload.cliente = null;
        }
        delete payload.marcar_como_pago;
        return payload;
    };

    const offerCobrancaWhatsApp = (saved, successMessage) => {
        const status = String(saved?.status || '').toUpperCase();
        const canOffer =
            saved?.id
            && saved?.tipo === 'receita'
            && saved?.eh_cobranca
            && status === 'PENDENTE';
        if (!canOffer) {
            onSave(true, successMessage);
            return;
        }
        onSave(true, successMessage);
        modal.confirm({
            title: 'Gerar cobrança no WhatsApp?',
            content: 'Sua receita foi salva como cobrança. Deseja gerar o lembrete com Pix agora?',
            okText: 'Gerar cobrança',
            cancelText: 'Agora não',
            centered: true,
            onOk: () => {
                setTransacaoCobranca(saved);
                setIsCobrancaModalOpen(true);
            },
        });
    };

    const onFinish = async (values) => {
        setError('');
        const parcelado = values.parcelado && !isEditing && (formType === 'receita' || formType === 'despesa');
        if (parcelado) {
            const n = parseInt(values.total_parcelas, 10);
            if (isNaN(n) || n < 2) {
                setError('Informe pelo menos 2 parcelas.');
                return;
            }
            const vTotal = modoParcelado === 'valor_total' ? parseFloat(values.valor_total_parcelado) : null;
            const vParcela = modoParcelado === 'valor_parcela' ? parseFloat(values.valor_parcela) : null;
            if (modoParcelado === 'valor_total' && (isNaN(vTotal) || vTotal <= 0)) {
                setError('Informe o valor total.');
                return;
            }
            if (modoParcelado === 'valor_parcela' && (isNaN(vParcela) || vParcela <= 0)) {
                setError('Informe o valor de cada parcela.');
                return;
            }
        } else {
            const valorNumerico = parseFloat(values.valor);
            if (isNaN(valorNumerico) || valorNumerico <= 0) {
                setError('O valor informado é inválido ou igual a zero.');
                return;
            }
        }
        setIsLoading(true);
        try {
            if (isEditing) {
                const payload = buildPayload(values);
                const updated = await updateTransacao(transacaoToEdit.id, payload);
                offerCobrancaWhatsApp(
                    { ...payload, ...updated, id: updated?.id ?? transacaoToEdit.id, tipo: formType },
                    'Transação salva com sucesso!',
                );
            } else if (parcelado) {
                const payload = {
                    descricao: values.descricao,
                    tipo: formType,
                    modo: modoParcelado,
                    total_parcelas: parseInt(values.total_parcelas, 10),
                    data_primeira_parcela: values.data_vencimento ? dayjs(values.data_vencimento).format('YYYY-MM-DD') : null,
                    conta_id: values.conta,
                    categoria_id: values.categoria,
                    marcar_primeira_como_pago: !!values.marcar_como_pago,
                    data_pagamento: values.marcar_como_pago && values.data_pagamento ? dayjs(values.data_pagamento).format('YYYY-MM-DD') : null,
                };
                if (modoParcelado === 'valor_total') {
                    payload.valor_total = parseFloat(values.valor_total_parcelado);
                } else {
                    payload.valor_parcela = parseFloat(values.valor_parcela);
                }
                await createLancamentoParceladoConta(payload);
                onSave(true, `Lançamento parcelado criado: ${payload.total_parcelas} parcelas.`);
            } else {
                const payload = buildPayload(values);
                const created = await createTransacao(payload);
                offerCobrancaWhatsApp(
                    { ...payload, ...created, id: created?.id, tipo: formType },
                    'Transação salva com sucesso!',
                );
            }
        } catch (err) {
            console.error("Erro ao salvar transação:", err);
            setError(getErrorMessage(err, 'Ocorreu um erro ao salvar.'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveAndNew = async () => {
        try {
            const values = await form.validateFields();
            setError('');
            const valorNumerico = parseFloat(values.valor);
            if (isNaN(valorNumerico) || valorNumerico <= 0) {
                setError('O valor informado é inválido ou igual a zero.');
                return;
            }
            setIsSavingAndNew(true);
            const payload = buildPayload(values);
            await createTransacao(payload);
            onSave(false, 'Transação criada com sucesso!');
            form.resetFields(['descricao', 'valor', 'categoria', 'eh_cobranca', 'nome_devedor', 'cliente']);
            setEhCobranca(false);
            setSearchTerm('');
            form.setFieldsValue({ data_vencimento: dayjs(), valor: 0, marcar_como_pago: false });
        } catch (err) {
            if (err?.errorFields) return;
            console.error("Erro ao salvar transação:", err);
            setError(getErrorMessage(err, 'Ocorreu um erro ao salvar.'));
        } finally {
            setIsSavingAndNew(false);
        }
    };

    const handleCancel = () => { !(isLoading || isFetchingDetails) && onClose(); };
    const filterOption = (input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase());

    // 2. ESTILOS RESPONSIVOS E CONFIGURAÇÕES
    const inputSize = isMobile ? "large" : undefined;
    const modalWidth = isMobile ? '100%' : 520;
    const sheetTitle = isEditing ? 'Editar Lançamento' : 'Novo Lançamento';
    const saveBusy = isLoading || isFetchingDetails || isSavingAndNew;

    const formContent = (
            <Spin spinning={isLoadingData || isFetchingDetails}>
                <Form form={form} layout="vertical" onFinish={onFinish} onValuesChange={handleFormValuesChange} scrollToFirstError initialValues={{ tipo: 'despesa', data_vencimento: dayjs(), marcar_como_pago: false, valor: 0, eh_cobranca: false, parcelado: false, modo_parcelado: 'valor_total', total_parcelas: 2 }}>
                    {/* 4. RADIO GROUP RESPONSIVO */}
                    <Form.Item name="tipo" label="Tipo de Lançamento" rules={[{ required: true }]}>
                        <Radio.Group disabled={isEditingTransfer} style={isMobile ? { width: '100%', display: 'flex' } : {}}>
                            <Radio.Button value="despesa" style={isMobile ? { flex: 1, textAlign: 'center', padding: '0 4px', fontSize: '13px', whiteSpace: 'nowrap' } : {}}>Despesa</Radio.Button>
                            <Radio.Button value="receita" style={isMobile ? { flex: 1, textAlign: 'center', padding: '0 4px', fontSize: '13px', whiteSpace: 'nowrap' } : {}}>Receita</Radio.Button>
                            <Radio.Button value="transferencia" style={isMobile ? { flex: 1, textAlign: 'center', padding: '0 4px', fontSize: '13px', whiteSpace: 'nowrap' } : {}}>Transferência</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    
                    {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

                    {metaVinculada && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={
                                metaVinculada.movimento === 'resgate'
                                    ? `Resgate ligado à meta “${metaVinculada.nome || 'Poupança'}”`
                                    : `Aporte ligado à meta “${metaVinculada.nome || 'Poupança'}”`
                            }
                            description="Alterar o valor atualiza o guardado na caixinha. As contas não podem ser trocadas por aqui."
                        />
                    )}
                    
                    <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Por favor, insira a descrição!' }]}>
                        <AutoComplete
                            options={suggestions}
                            onSearch={handleSearch}
                            onSelect={handleSelect}
                            filterOption={false}
                            placeholder="Digite para buscar..."
                            size={inputSize}
                            getPopupContainer={modalPopupContainer}
                        >
                           <Input size={inputSize} onFocus={scrollFieldIntoView} />
                        </AutoComplete>
                    </Form.Item>

                    {/* Parcelado em conta: só para receita/despesa e novo lançamento */}
                    {!isEditing && (formType === 'receita' || formType === 'despesa') && (
                        <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 10, border: '1px solid #e8e8e8', background: 'var(--color-surface-muted, #F8F4EC)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isParcelado ? 14 : 0, flexWrap: 'wrap', gap: 8 }}>
                                <span style={{ fontWeight: 500, color: '#262626', fontSize: 14 }}>Dividir em parcelas</span>
                                <Form.Item name="parcelado" valuePropName="checked" noStyle>
                                    <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                                </Form.Item>
                            </div>
                            {isParcelado && (
                                <>
                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: 12, color: '#595959', marginBottom: 6 }}>Como deseja informar o valor?</div>
                                        <Form.Item name="modo_parcelado" noStyle>
                                            <Radio.Group
                                                optionType="button"
                                                buttonStyle="solid"
                                                size={inputSize}
                                                options={[
                                                    { label: 'Valor total', value: 'valor_total' },
                                                    { label: 'Valor da parcela', value: 'valor_parcela' },
                                                ]}
                                                onChange={(e) => setModoParcelado(e.target.value)}
                                            />
                                        </Form.Item>
                                    </div>
                                    {/* Ambos montados — hidden evita remount do InputNumber ao trocar modo */}
                                    <Form.Item
                                        name="valor_total_parcelado"
                                        label="Valor total"
                                        hidden={modoParcelado !== 'valor_total'}
                                        rules={modoParcelado === 'valor_total' ? [{ required: true, message: 'Informe o valor total.' }] : []}
                                    >
                                        <InputNumber size={inputSize} {...moneyInputProps} onFocus={scrollFieldIntoView} />
                                    </Form.Item>
                                    <Form.Item
                                        name="valor_parcela"
                                        label="Valor de cada parcela"
                                        hidden={modoParcelado !== 'valor_parcela'}
                                        rules={modoParcelado === 'valor_parcela' ? [{ required: true, message: 'Informe o valor da parcela.' }] : []}
                                    >
                                        <InputNumber size={inputSize} {...moneyInputProps} onFocus={scrollFieldIntoView} />
                                    </Form.Item>
                                    <Form.Item
                                        name="total_parcelas"
                                        label={modoParcelado === 'valor_total' ? 'Em quantas parcelas?' : 'Quantas vezes?'}
                                        rules={[{ required: true, message: 'Informe o número de parcelas.' }, { type: 'number', min: 2, max: 120, message: 'Entre 2 e 120 parcelas.' }]}
                                    >
                                        <InputNumber min={2} max={120} size={inputSize} style={{ width: '100%' }} placeholder="Ex: 5" inputMode="numeric" onFocus={scrollFieldIntoView} />
                                    </Form.Item>
                                    <ParceladoResumo form={form} modoParcelado={modoParcelado} formatCurrency={formatCurrency} />
                                </>
                            )}
                        </div>
                    )}

                    {/* Campo valor sempre montado — hidden quando parcelado evita remount */}
                    <Form.Item
                        name="valor"
                        label="Valor"
                        hidden={isParcelado}
                        rules={isParcelado ? [] : [{ required: true, message: 'O valor é obrigatório!' }]}
                    >
                        <InputNumber size={inputSize} {...moneyInputProps} onFocus={scrollFieldIntoView} />
                    </Form.Item>

                    {formType === 'receita' && (
                        <div style={{ marginBottom: 16, background: 'var(--color-surface-muted, #F8F4EC)', padding: '12px 16px', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: ehCobranca ? 12 : 0, gap: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 500, color: '#333', fontSize: isMobile ? '13px' : '14px' }}>
                                        É uma cobrança?
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #5A6A6A)', marginTop: 2 }}>
                                        Libera lembrete no WhatsApp com Pix
                                    </div>
                                </div>
                                <Form.Item name="eh_cobranca" valuePropName="checked" noStyle>
                                    <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                                </Form.Item>
                            </div>
                            {ehCobranca && (
                                <>
                                    {modoPrestador && (
                                        <Form.Item name="cliente" label="Cliente" style={{ marginBottom: 12 }}>
                                            <Select
                                                allowClear
                                                showSearch
                                                placeholder="Selecionar cliente (opcional)"
                                                optionFilterProp="children"
                                                size={inputSize}
                                                getPopupContainer={modalPopupContainer}
                                                filterOption={(input, option) =>
                                                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {clientes.map((c) => (
                                                    <Option key={c.id} value={c.id}>{c.nome}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                    <Form.Item name="nome_devedor" label="Nome do Pagador/Devedor" rules={[{ required: true, message: 'Informe quem deve pagar.' }]} style={{ marginBottom: 0 }}>
                                        <Input placeholder="Ex: João da Silva" size={inputSize} onFocus={scrollFieldIntoView} />
                                    </Form.Item>
                                </>
                            )}
                        </div>
                    )}
                    
                    {isTransferencia ? (
                        <Row gutter={16}>
                            <Col span={isMobile ? 24 : 12}>
                                <Form.Item name="conta" label="Conta de Origem" rules={[{ required: true, message: 'Selecione a conta de origem!' }]}>
                                    <Select
                                        showSearch
                                        placeholder="Selecione..."
                                        optionFilterProp="children"
                                        filterOption={filterOption}
                                        size={inputSize}
                                        disabled={!!metaVinculada}
                                        getPopupContainer={modalPopupContainer}
                                    >
                                        {[...contas].sort((a, b) => a.nome.localeCompare(b.nome)).map(c => (
                                            <Option key={c.id} value={c.id}>
                                                {`${c.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.saldo_atual)})`}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={isMobile ? 24 : 12}>
                                <Form.Item name="conta_destino" label="Conta de Destino" rules={[{ required: true, message: 'Selecione a conta de destino!' }]}>
                                    <Select
                                        showSearch
                                        placeholder="Selecione..."
                                        optionFilterProp="children"
                                        filterOption={filterOption}
                                        size={inputSize}
                                        disabled={!!metaVinculada}
                                        getPopupContainer={modalPopupContainer}
                                    >
                                        {[...contas].filter(c => c.id !== form.getFieldValue('conta')).sort((a, b) => a.nome.localeCompare(b.nome)).map(c => <Option key={c.id} value={c.id}>{c.nome}</Option>)}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    ) : (
                        <Row gutter={16}>
                             <Col span={isMobile ? 24 : 12}>
                                <Form.Item name="conta" label="Conta" rules={[{ required: true, message: 'Por favor, selecione a conta!' }]}>
                                    <Select showSearch placeholder="Selecione..." optionFilterProp="children" filterOption={filterOption} size={inputSize} getPopupContainer={modalPopupContainer}>
                                        {[...contas].sort((a, b) => a.nome.localeCompare(b.nome)).map(c => (
                                            <Option key={c.id} value={c.id}>
                                                {formType === 'despesa'
                                                    ? `${c.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.saldo_atual)})`
                                                    : c.nome}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                             </Col>
                             <Col span={isMobile ? 24 : 12}>
                                <Form.Item name="categoria" label="Categoria" rules={[{ required: true, message: 'Por favor, selecione a categoria!' }]}>
                                    <Select showSearch placeholder="Selecione..." optionFilterProp="children" filterOption={filterOption} size={inputSize} getPopupContainer={modalPopupContainer}>
                                        {allCategorias.filter(c => c.tipo === formType).map(c => <Option key={c.id} value={c.id}>{c.nome}</Option>)}
                                    </Select>
                                </Form.Item>
                             </Col>
                        </Row>
                    )}
                    
                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item name="data_vencimento" label={isParcelado ? 'Data da 1ª parcela' : 'Data de Vencimento'} rules={[{ required: true, message: 'Por favor, insira a data!' }]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Selecione a data" size={inputSize} getPopupContainer={modalPopupContainer} onFocus={scrollFieldIntoView} />
                            </Form.Item>
                        </Col>
                        {marcarComoPago && !isTransferencia && (
                            <Col span={isMobile ? 24 : 12}>
                                <Form.Item name="data_pagamento" label="Data de Pagamento" rules={[{ required: true, message: 'A data de pagamento é obrigatória.' }]}>
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Selecione a data" size={inputSize} getPopupContainer={modalPopupContainer} onFocus={scrollFieldIntoView} />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                    
                    {!isTransferencia && (
                        <Form.Item name="marcar_como_pago" valuePropName="checked">
                            <Checkbox style={isMobile ? { fontSize: '16px' } : {}}>{formType === 'receita' ? 'Marcar como Recebida' : 'Marcar como Paga'}</Checkbox>
                        </Form.Item>
                    )}

                    {isEditing && lancadoPor && (
                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--color-text-secondary, #8a8a8a)' }}>
                            <UserOutlined style={{ marginRight: 6 }} />
                            Lançado por {lancadoPor}
                        </div>
                    )}
                </Form>
            </Spin>
    );

    const nestedModals = (
        <>
        <CobrancaModal
            visible={isCobrancaModalOpen}
            onClose={() => {
                setIsCobrancaModalOpen(false);
                setTransacaoCobranca(null);
            }}
            transacao={transacaoCobranca}
            userPix={user?.chave_pix_padrao}
            userBanco={user?.banco_preferencia}
        />
        <PixCobrancaSetupModal
            open={isPixSetupOpen}
            onClose={() => setIsPixSetupOpen(false)}
            isMobile={isMobile}
        />
        </>
    );

    if (isMobile) {
        return (
            <>
                <MobileLaunchSheet
                    open={isOpen}
                    title={sheetTitle}
                    onCancel={handleCancel}
                    onSave={() => form.submit()}
                    saveLabel="Salvar"
                    saveLoading={isLoading || isFetchingDetails}
                    saveDisabled={isSavingAndNew}
                    cancelDisabled={saveBusy}
                    secondaryAction={
                        !isEditing && !isParcelado ? (
                            <Button
                                type="default"
                                loading={isSavingAndNew}
                                onClick={handleSaveAndNew}
                                disabled={isLoading || isFetchingDetails}
                                block
                                size="large"
                            >
                                Salvar e Adicionar Outro
                            </Button>
                        ) : null
                    }
                >
                    {formContent}
                </MobileLaunchSheet>
                {nestedModals}
            </>
        );
    }

    return (
        <>
        <Modal
            open={isOpen}
            title={sheetTitle}
            onCancel={handleCancel}
            destroyOnClose 
            width={modalWidth}
            footer={[
                <Button key="back" onClick={handleCancel} disabled={saveBusy}>Cancelar</Button>,
                !isEditing && !isParcelado && (
                    <Button key="saveAndNew" type="default" loading={isSavingAndNew} onClick={handleSaveAndNew} disabled={isLoading || isFetchingDetails}>
                        Salvar e Adicionar Outro
                    </Button>
                ),
                <Button key="submit" type="primary" loading={isLoading || isFetchingDetails} onClick={() => form.submit()} disabled={isSavingAndNew}>
                    {isEditing ? 'Salvar Alterações' : 'Salvar'}
                </Button>,
            ]}
        >
            {formContent}
        </Modal>
        {nestedModals}
        </>
    );
};

export default TransacaoModal;